import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Header from '../../components/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import ImpactSummaryCard from './components/ImpactSummaryCard';
import SkillProgressCard from './components/SkillProgressCard';
import CertificateCard from './components/CertificateCard';
import TimelineEvent from './components/TimelineEvent';
import AchievementBadge from './components/AchievementBadge';
import ConnectionCard from './components/ConnectionCard';
import ImpactChart from './components/ImpactChart';
import MilestoneCard from './components/MilestoneCard';
import CertificateModal from '../../components/CertificateModal';

const VolunteerJourneyTracker = () => {
  const { user } = useAuth();

  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPeriod, setSelectedPeriod] = useState('all-time');
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [viewingCertificate, setViewingCertificate] = useState(null);

  // Default for new users
  const stats = user?.impactStats || { totalHours: 0, completed: 0, impactScore: 0 };
  const userSkills = user?.skills || [];

  const impactSummary = [
    {
      icon: 'Clock',
      label: 'Total Hours',
      value: stats.totalHours.toString(),
      subtext: 'Since Joining',
      trend: stats.monthlyHours ? Math.ceil((stats.monthlyHours / (stats.totalHours || 1)) * 100) : 0,
      color: 'primary'
    },
    {
      icon: 'Award',
      label: 'Events Completed', // Changed from Certificates to match DB data better or we can mock certificates count based on events
      value: stats.completed.toString(),
      subtext: 'Verified events',
      trend: 10,
      color: 'accent'
    },
    {
      icon: 'Zap',
      label: 'Skills Developed',
      value: userSkills.length.toString(),
      subtext: `${userSkills.filter(s => s.level >= 3).length} expert level`,
      trend: 5,
      color: 'secondary'
    },
    {
      icon: 'Users',
      label: 'Impact Score', // Changed from connections as we don't have connection data yet
      value: stats.impactScore.toString(),
      subtext: 'Community points',
      trend: 12,
      color: 'success'
    }];


  // Map real user skills to component format
  // If user has no skills (new user), show empty list or a prompt?
  // The mock data had "Project Management" etc. providing a "standard website look".
  // But user said "keep it zero for the new users".
  // So for new users (userSkills.length === 0), we show empty properly.
  // For the Demo user, we need to populate skills in the seed or just use the logic below.

  const skills = userSkills.length > 0 ? userSkills.map(s => ({
    skill: s.name,
    level: s.level >= 4 ? 'Expert' : s.level >= 3 ? 'Advanced' : 'Intermediate',
    progress: s.level * 20, // assumption level 1-5
    endorsements: Math.floor(Math.random() * 10), // mock
    icon: s.icon || 'Star'
  })) : []; // Empty for new users


  const userCertificates = user?.certificates || [];

  const certificates = userCertificates.length > 0 ? userCertificates.map((cert, index) => ({
    id: index,
    title: cert.title,
    issuer: cert.issuer,
    issueDate: new Date(cert.date).toLocaleDateString(),
    expiryDate: null,
    image: cert.url || "https://img.rocket.new/generatedImages/rocket_gen_img_117cc9ef1-1764668331007.png",
    imageAlt: 'Certificate',
    userName: user.name,
    date: cert.date
  })) : [];


  const timelineEvents = [
    {
      type: 'milestone',
      title: '300 Hours Milestone Achieved',
      description: 'Reached a major milestone in volunteer service hours, demonstrating consistent commitment to community impact.',
      date: 'Dec 10, 2024',
      impact: 'Impacted 150+ community members'
    },
    {
      type: 'certificate',
      title: 'Nonprofit Leadership Certification',
      description: 'Completed comprehensive training program in nonprofit management and leadership development.',
      date: 'Nov 15, 2024',
      organization: 'Community Impact Foundation',
      organizationLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_1b508d725-1765949143308.png",
      organizationLogoAlt: 'Community Impact Foundation logo with green tree symbol on white circular background'
    },
    {
      type: 'volunteer',
      title: 'Led Community Food Drive',
      description: 'Organized and coordinated a successful food drive that collected 2,000+ pounds of food for local families.',
      date: 'Nov 3, 2024',
      organization: 'City Food Bank',
      organizationLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_191f09d22-1765949142873.png",
      organizationLogoAlt: 'City Food Bank logo featuring orange wheat stalks on blue circular badge',
      impact: 'Served 500+ families'
    },
    {
      type: 'skill',
      title: 'Advanced Event Management Skills',
      description: 'Completed advanced training in event planning, logistics, and volunteer coordination.',
      date: 'Oct 8, 2024',
      organization: 'VolunteerHub Academy',
      organizationLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_1d410d5f6-1764859983253.png",
      organizationLogoAlt: 'VolunteerHub Academy logo with graduation cap icon on purple gradient background'
    },
    {
      type: 'connection',
      title: 'Mentorship Program Started',
      description: 'Began mentoring three new volunteers in community outreach and engagement strategies.',
      date: 'Sep 15, 2024',
      impact: '3 mentees onboarded'
    },
    {
      type: 'volunteer',
      title: 'Youth Education Workshop Series',
      description: 'Facilitated 8-week workshop series teaching digital literacy skills to underserved youth.',
      date: 'Aug 20, 2024',
      organization: 'Tech for Tomorrow',
      organizationLogo: "https://img.rocket.new/generatedImages/rocket_gen_img_1f8fd578f-1765949142403.png",
      organizationLogoAlt: 'Tech for Tomorrow logo with computer monitor icon on teal circular background',
      impact: '45 students trained'
    }];


  const achievements = [
    {
      id: 1,
      name: 'Community Champion',
      description: '300+ volunteer hours',
      icon: 'Trophy',
      rarity: 'legendary',
      earnedDate: 'Dec 10, 2024',
      isNew: true
    },
    {
      id: 2,
      name: 'Skill Master',
      description: '5 expert-level skills',
      icon: 'Zap',
      rarity: 'epic',
      earnedDate: 'Nov 28, 2024',
      isNew: false
    },
    {
      id: 3,
      name: 'Mentor Leader',
      description: 'Mentored 10+ volunteers',
      icon: 'Users',
      rarity: 'rare',
      earnedDate: 'Nov 15, 2024',
      isNew: false
    },
    {
      id: 4,
      name: 'Event Organizer',
      description: 'Led 5 major events',
      icon: 'Calendar',
      rarity: 'rare',
      earnedDate: 'Oct 22, 2024',
      isNew: false
    },
    {
      id: 5,
      name: 'Quick Starter',
      description: 'First 10 hours completed',
      icon: 'Rocket',
      rarity: 'common',
      earnedDate: 'Jan 15, 2024',
      isNew: false
    },
    {
      id: 6,
      name: 'Certified Pro',
      description: '10 certifications earned',
      icon: 'Award',
      rarity: 'epic',
      earnedDate: 'Nov 5, 2024',
      isNew: false
    }];


  const connections = [
    {
      id: 1,
      name: 'Sarah Martinez',
      role: 'Community Outreach Director',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d0b00451-1763296757868.png",
      avatarAlt: 'Professional headshot of Hispanic woman with long dark hair wearing blue blazer and warm smile',
      isOnline: true,
      mutualConnections: 12,
      connectedSince: '8 months ago'
    },
    {
      id: 2,
      name: 'James Chen',
      role: 'Volunteer Coordinator',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b76d09ba-1763296280585.png",
      avatarAlt: 'Professional photo of Asian man with short black hair in navy suit with friendly expression',
      isOnline: false,
      mutualConnections: 8,
      connectedSince: '6 months ago'
    },
    {
      id: 3,
      name: 'Emily Rodriguez',
      role: 'Event Planning Specialist',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_11176ce80-1763296378826.png",
      avatarAlt: 'Headshot of young Latina woman with curly brown hair wearing green top with bright smile',
      isOnline: true,
      mutualConnections: 15,
      connectedSince: '10 months ago'
    },
    {
      id: 4,
      name: 'Michael Thompson',
      role: 'Nonprofit Consultant',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1f23d9c3d-1763295425663.png",
      avatarAlt: 'Professional portrait of African American man with short hair in gray suit with confident expression',
      isOnline: false,
      mutualConnections: 6,
      connectedSince: '4 months ago'
    }];


  const chartData = [
    { month: 'Jan', hours: 28 },
    { month: 'Feb', hours: 32 },
    { month: 'Mar', hours: 25 },
    { month: 'Apr', hours: 38 },
    { month: 'May', hours: 42 },
    { month: 'Jun', hours: 35 },
    { month: 'Jul', hours: 45 },
    { month: 'Aug', hours: 40 },
    { month: 'Sep', hours: 30 },
    { month: 'Oct', hours: 36 },
    { month: 'Nov', hours: 44 },
    { month: 'Dec', hours: 47 }];


  const milestones = [
    {
      icon: 'Clock',
      title: '500 Hour Hero',
      description: 'Complete 500 total volunteer hours',
      current: stats.totalHours,
      target: 500,
      unit: 'hours',
      reward: 'Exclusive Hero Badge & Feature Story'
    },
    {
      icon: 'Award',
      title: 'Certification Master',
      description: 'Earn 20 professional certifications',
      current: 0, // No cert data yet
      target: 20,
      unit: 'certificates',
      reward: 'Master Certificate & LinkedIn Endorsement'
    },
    {
      icon: 'Users',
      title: 'Community Builder',
      description: 'Build a network of 100 connections',
      current: 0, // No connection data yet
      target: 100,
      unit: 'connections',
      reward: 'Networking Event Access & Mentor Badge'
    }];


  const periodOptions = [
    { value: 'all-time', label: 'All Time' },
    { value: 'this-year', label: 'This Year' },
    { value: 'this-month', label: 'This Month' },
    { value: 'last-30-days', label: 'Last 30 Days' }];


  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'timeline', label: 'Timeline', icon: 'Clock' },
    { id: 'skills', label: 'Skills', icon: 'Zap' },
    { id: 'certificates', label: 'Certificates', icon: 'Award' },
    { id: 'achievements', label: 'Achievements', icon: 'Trophy' },
    { id: 'connections', label: 'Connections', icon: 'Users' }];


  const handleDownloadCertificate = (certificate) => {
    setViewingCertificate(certificate);
  };

  const handleShareCertificate = (certificateId) => {
    console.log('Sharing certificate:', certificateId);
  };

  const handleBadgeClick = (badge) => {
    setSelectedBadge(badge);
  };

  const handleMessageConnection = (connectionId) => {
    console.log('Messaging connection:', connectionId);
  };

  const handleViewProfile = (connectionId) => {
    console.log('Viewing profile:', connectionId);
  };

  const handleDownloadReport = () => {
    alert("Generating Impact Report...\n\nYour PDF report will download shortly.");
  };

  const handleShareProfile = () => {
    alert("Profile link copied to clipboard!\n\nShare your journey on social media to inspire others.");
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="main-content">
        <div className="bg-gradient-to-br from-primary to-secondary text-white py-12 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
              <div className="flex-1">
                <h1 className="text-4xl font-bold mb-3">Your Volunteer Journey</h1>
                <p className="text-lg opacity-90 mb-4">
                  Track your impact, celebrate achievements, and continue making a difference
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button
                    variant="secondary"
                    iconName="Download"
                    iconPosition="left"
                    onClick={handleDownloadReport}>

                    Download Report
                  </Button>
                  <Button
                    variant="outline"
                    iconName="Share2"
                    iconPosition="left"
                    onClick={handleShareProfile}
                    className="bg-white/10 border-white/30 text-white hover:bg-white/20">

                    Share Profile
                  </Button>
                </div>
              </div>
              <div className="w-full lg:w-64">
                <Select
                  options={periodOptions}
                  value={selectedPeriod}
                  onChange={setSelectedPeriod}
                  placeholder="Select time period" />

              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="mb-8">
            <div className="border-b border-border overflow-x-auto">
              <div className="flex gap-1 min-w-max">
                {tabs?.map((tab) =>
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-6 py-3 font-medium transition-all border-b-2 ${activeTab === tab?.id ?
                      'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'}`
                    }>

                    <Icon name={tab?.icon} size={18} />
                    <span>{tab?.label}</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {activeTab === 'overview' &&
            <div className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {impactSummary?.map((item, index) =>
                  <ImpactSummaryCard key={index} {...item} />
                )}
              </div>

              <ImpactChart data={chartData} title="Volunteer Hours Over Time" />

              <div>
                <h2 className="text-2xl font-bold text-foreground mb-6">Active Milestones</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {milestones?.map((milestone, index) =>
                    <MilestoneCard
                      key={index}
                      milestone={milestone}
                      isCompleted={milestone?.current >= milestone?.target} />

                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-foreground">Recent Achievements</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setActiveTab('achievements')}>

                    View All
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {achievements?.slice(0, 6)?.map((badge) =>
                    <AchievementBadge
                      key={badge?.id}
                      badge={badge}
                      onClick={handleBadgeClick} />

                  )}
                </div>
              </div>
            </div>
          }

          {activeTab === 'timeline' &&
            <div className="max-w-4xl mx-auto">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-foreground mb-2">Your Impact Timeline</h2>
                <p className="text-muted-foreground">
                  A chronological view of your volunteer journey and achievements
                </p>
              </div>
              <div className="space-y-0">
                {timelineEvents?.map((event, index) =>
                  <TimelineEvent
                    key={index}
                    event={event}
                    isLast={index === timelineEvents?.length - 1} />

                )}
              </div>
            </div>
          }

          {activeTab === 'skills' &&
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-foreground mb-2">Skills Portfolio</h2>
                <p className="text-muted-foreground">
                  Track your skill development and professional growth through volunteering
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {skills?.map((skill, index) =>
                  <SkillProgressCard key={index} {...skill} />
                )}
              </div>
              <div className="mt-8 text-center">
                <Button
                  variant="outline"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => navigate('/skills-development-academy')}>

                  Explore More Skills
                </Button>
              </div>
            </div>
          }

          {activeTab === 'certificates' &&
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-foreground mb-2">Professional Certificates</h2>
                <p className="text-muted-foreground">
                  Your verified credentials and professional development achievements
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {certificates?.map((certificate) =>
                  <CertificateCard
                    key={certificate?.id}
                    certificate={certificate}
                    onDownload={handleDownloadCertificate}
                    onShare={handleShareCertificate} />

                )}
              </div>
            </div>
          }

          {activeTab === 'achievements' &&
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-foreground mb-2">Achievement Gallery</h2>
                <p className="text-muted-foreground">
                  Celebrate your milestones and unlock new badges as you grow
                </p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                {achievements?.map((badge) =>
                  <AchievementBadge
                    key={badge?.id}
                    badge={badge}
                    onClick={handleBadgeClick} />

                )}
              </div>
            </div>
          }

          {activeTab === 'connections' &&
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-foreground mb-2">Community Connections</h2>
                <p className="text-muted-foreground">
                  Your network of fellow volunteers, coordinators, and mentors
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {connections?.map((connection) =>
                  <ConnectionCard
                    key={connection?.id}
                    connection={connection}
                    onMessage={handleMessageConnection}
                    onViewProfile={handleViewProfile} />

                )}
              </div>
              <div className="mt-8 text-center">
                <Button
                  variant="outline"
                  iconName="UserPlus"
                  iconPosition="left">

                  Find More Connections
                </Button>
              </div>
            </div>
          }
        </div>
      </main>
      {selectedBadge &&
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedBadge(null)}>

          <div
            className="bg-card rounded-2xl p-8 max-w-md w-full shadow-2xl"
            onClick={(e) => e?.stopPropagation()}>

            <div className="text-center">
              <div className={`w-32 h-32 mx-auto mb-6 rounded-full bg-gradient-to-br ${selectedBadge?.rarity === 'legendary' ? 'from-yellow-400 to-yellow-600' :
                selectedBadge?.rarity === 'epic' ? 'from-purple-400 to-purple-600' :
                  selectedBadge?.rarity === 'rare' ? 'from-blue-400 to-blue-600' : 'from-gray-400 to-gray-600'} flex items-center justify-center shadow-2xl`
              }>
                <Icon name={selectedBadge?.icon} size={64} color="white" />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-2">{selectedBadge?.name}</h3>
              <p className="text-muted-foreground mb-4">{selectedBadge?.description}</p>
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-6">
                <Icon name="Calendar" size={16} />
                <span>Earned on {selectedBadge?.earnedDate}</span>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  fullWidth
                  iconName="Share2"
                  iconPosition="left"
                  onClick={() => console.log('Sharing badge:', selectedBadge?.id)}>

                  Share
                </Button>
                <Button
                  variant="default"
                  fullWidth
                  onClick={() => setSelectedBadge(null)}>

                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      }
      {viewingCertificate && (
        <CertificateModal
          certificate={viewingCertificate}
          onClose={() => setViewingCertificate(null)}
        />
      )}

      <footer className="bg-card border-t border-border mt-16">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              © {new Date()?.getFullYear()} VolunteerHub. Empowering communities through meaningful connections.
            </p>
            <div className="flex items-center gap-6">
              <button className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Privacy Policy
              </button>
              <button className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Terms of Service
              </button>
              <button className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>);
};

export default VolunteerJourneyTracker;